import { useEffect, useState } from 'react';
import { SEO, BreadcrumbSchema, SpeakableSchema, FaqSchema } from '@/components/SEO';
import { Helmet } from 'react-helmet-async';
import { useInView } from 'react-intersection-observer';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { CTABanner } from '@/components/CTABanner';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight, Phone, CheckCircle2, Shield, Award, Star, MapPin
} from 'lucide-react';

/* Shared slideshow image pool — used on every town page */
import slide1 from '@/assets/deckpro (1).jpeg';
import slide2 from '@/assets/deckpro (5).jpeg';
import slide3 from '@/assets/deckpro (7).jpeg';
import slide4 from '@/assets/deckpro (9).jpeg';
import slide5 from '@/assets/deckpro (11).jpeg';
import slide6 from '@/assets/deckpro (15).jpeg';
import slide7 from '@/assets/deckpro (18).jpeg';
import slide8 from '@/assets/deckpro (24).jpeg';
import slide9 from '@/assets/deckpro (26).jpeg';
import slide10 from '@/assets/deckpro (28).jpeg';
import slide11 from '@/assets/deckpro (31).jpeg';
import slide12 from '@/assets/deckpro (35).jpeg';
import slide13 from '@/assets/deckpro (38).jpeg';
import slide14 from '@/assets/deckpro (41).jpeg';

const slideshowImages = [slide1, slide2, slide3, slide4, slide5, slide6, slide7, slide8, slide9, slide10, slide11, slide12, slide13, slide14];

export interface TownPageData {
  town: string;
  county: string;
  countySlug: string;
  slug: string;
  description: string;
  images: string[];
  imageAlts: string[];
}

const services = [
  { title: 'Custom Deck Design & Build', desc: 'Personalized deck designs, 3D renderings, material selection, and full-service construction.' },
  { title: 'Deck Repair & Restoration', desc: 'Board replacement, structural repairs, staining, sealing, and safety inspections.' },
  { title: 'Complete Deck Replacement', desc: 'Full demolition and rebuild with upgraded materials and modern design.' },
  { title: 'Composite & PVC Decking', desc: 'Trex, TimberTech, Azek install — low-maintenance with 25+ year warranties.' },
  { title: 'Wood Decking Specialists', desc: 'Cedar, redwood, pressure-treated pine, and exotic hardwoods like Ipe & Tigerwood.' },
  { title: 'Outdoor Living', desc: 'Pergolas, cable railings, LED lighting, outdoor kitchens, and fire features.' },
];

const reasons = [
  { icon: Shield, title: 'Licensed & Insured', desc: 'Fully licensed NJ contractor with liability and workers\' comp coverage.' },
  { icon: Award, title: '25+ Years Experience', desc: 'Over two decades of professional deck building across New Jersey.' },
  { icon: Star, title: 'Lifetime Warranty', desc: 'Industry-leading lifetime workmanship guarantee on every deck.' },
  { icon: CheckCircle2, title: 'Free Estimates', desc: 'No-obligation on-site consultations with transparent, detailed pricing.' },
];

function Anim({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div
      ref={ref}
      className={`transition-all duration-600 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}

/* ─── Auto-rotating image slideshow ─────────────── */
function AutoSlideshow({ town }: { town: string }) {
  const images = slideshowImages;
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * images.length));

  useEffect(() => {
    if (images.length <= 1) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % images.length);
    }, 3000);
    return () => clearInterval(t);
  }, [images.length]);

  return (
    <div className="relative">
      <div className="relative aspect-[4/5] border-4 border-navy shadow-hard overflow-hidden bg-navy">
        <AnimatePresence mode="wait">
          <motion.img
            key={idx}
            src={images[idx]}
            alt={`Deck project in ${town}, NJ by DeckPros NJ — photo ${idx + 1}`}
            className="w-full h-full object-cover"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          />
        </AnimatePresence>

        {/* Location badge */}
        <div className="absolute top-3 left-3 bg-lime border-2 border-navy px-3 py-1.5 font-display font-bold text-[10px] uppercase tracking-[0.2em] text-navy flex items-center gap-1.5">
          <MapPin className="w-3 h-3" />
          {town}, NJ
        </div>

        {/* Counter */}
        <div className="absolute bottom-3 right-3 bg-navy border-2 border-lime text-lime px-2.5 py-1 font-display font-bold text-[10px] uppercase tracking-wider">
          {idx + 1} / {images.length}
        </div>
      </div>

      {/* Dot indicators */}
      <div className="flex flex-wrap justify-center gap-1.5 mt-4">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`View image ${i + 1}`}
            className={`h-1.5 transition-all duration-300 ${
              i === idx ? 'w-8 bg-lime' : 'w-1.5 bg-navy/20 hover:bg-navy/40'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default function TownPage({ data }: { data: TownPageData }) {
  const title = `Deck Builder in ${data.town}, NJ`;
  const metaDescription = `Professional deck builder serving ${data.town}, ${data.county}. Custom decks, composite (Trex/TimberTech) installs, repairs & replacements. Licensed, bonded & insured. Free quotes: (201) 657-0855.`;
  const canonical = `/areas/${data.slug}`;

  const faqs = [
    {
      question: `Do I need a permit to build a deck in ${data.town}, NJ?`,
      answer: `Yes — most deck projects in ${data.town} and ${data.county} require a building permit, especially decks attached to the home or over 30 inches above grade. DeckPros NJ handles all permit applications and inspections as part of every project.`,
    },
    {
      question: `How long does it take to build a deck in ${data.town}?`,
      answer: `Most residential decks in ${data.town} take 3–7 days to build once construction begins. Permitting typically adds 1–2 weeks upfront. Simple repairs can finish in 1–2 days. We provide a detailed timeline during your free estimate.`,
    },
    {
      question: `What decking materials do you recommend for ${data.county} homes?`,
      answer: `For ${data.county}, we often recommend composite decking (Trex, TimberTech, Azek) for its durability and low maintenance in New Jersey's freeze-thaw climate. Cedar and pressure-treated wood are excellent budget-friendly options. We'll help you pick during your consultation.`,
    },
    {
      question: `How much does a new deck cost in ${data.town}?`,
      answer: `Decks in ${data.town} typically range from $15–25 per sq ft for pressure-treated wood, $30–45/sq ft for composite, and $50–80+/sq ft for premium hardwoods. A standard 300 sq ft deck is usually $6,000–$20,000 installed. Exact pricing depends on size, materials, and features.`,
    },
    {
      question: `Are you available for deck repairs in ${data.town}?`,
      answer: `Yes. DeckPros NJ provides full repair services in ${data.town} — board replacement, railing fixes, structural reinforcement, staining, and rot remediation. Same-day emergency repairs are available for safety issues.`,
    },
  ];

  /* Per-town LocalBusiness schema */
  const townSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `https://deckprosnj.com${canonical}#business`,
    name: `DeckPros NJ — ${data.town} Deck Builder`,
    description: metaDescription,
    url: `https://deckprosnj.com${canonical}`,
    telephone: '+1-201-657-0855',
    address: {
      '@type': 'PostalAddress',
      addressLocality: data.town,
      addressRegion: 'NJ',
      addressCountry: 'US',
    },
    areaServed: {
      '@type': 'City',
      name: data.town,
      containedInPlace: { '@type': 'AdministrativeArea', name: data.county },
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '500',
      bestRating: '5',
    },
    priceRange: '$$',
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title={title}
        description={metaDescription}
        canonical={canonical}
        keywords={`deck builder ${data.town} NJ, ${data.town} deck contractor, ${data.town} deck repair, ${data.town} composite decking, ${data.county} deck builder, New Jersey deck builder`}
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: '/' },
        { name: 'Areas', url: '/areas' },
        { name: data.county, url: `/areas/${data.countySlug}` },
        { name: data.town, url: canonical },
      ]} />
      <SpeakableSchema />
      <FaqSchema faqs={faqs} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(townSchema)}</script>
      </Helmet>

      <Header />
      <main>
        {/* ─── Hero ─────────────────────────────────── */}
        <section className="relative pt-28 pb-14 bg-navy text-white overflow-hidden">
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <p className="section-eyebrow justify-center">
                Deck Contractor · {data.town}, NJ
              </p>
              <h1
                className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-4 uppercase leading-[1.05]"
                data-speakable
              >
                Deck Builder in <span className="text-lime">{data.town}</span>
              </h1>
              <p
                className="text-base md:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed font-body mb-3"
                data-speakable
              >
                {data.description}
              </p>
              <p className="text-xs text-white/50 font-display uppercase tracking-[0.2em] mb-6 flex items-center justify-center gap-2">
                <MapPin className="w-3 h-3 text-lime" />
                Serving {data.town} & all of {data.county}, New Jersey
              </p>

              <div className="flex flex-wrap justify-center gap-3 mb-8">
                <Link to="/contact">
                  <Button variant="cta" size="lg">
                    Get Free Estimate
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a href="tel:2016570855">
                  <Button variant="hero" size="lg">
                    <Phone className="w-4 h-4" />
                    (201) 657-0855
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Breadcrumb trail ──────────────────────── */}
        <nav
          aria-label="Breadcrumb"
          className="bg-cream-dark border-b-2 border-navy/10 py-3"
        >
          <div className="container mx-auto px-4">
            <ol className="flex flex-wrap items-center gap-2 text-[11px] font-display font-bold uppercase tracking-[0.15em] text-stone">
              <li><Link to="/" className="hover:text-lime transition-colors">Home</Link></li>
              <li className="text-stone/50">/</li>
              <li><Link to="/areas" className="hover:text-lime transition-colors">Areas</Link></li>
              <li className="text-stone/50">/</li>
              <li><Link to={`/areas/${data.countySlug}`} className="hover:text-lime transition-colors">{data.county}</Link></li>
              <li className="text-stone/50">/</li>
              <li className="text-lime">{data.town}</li>
            </ol>
          </div>
        </nav>

        {/* ─── About / Intro (with auto-slideshow) ───── */}
        <section className="section-compact bg-cream">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center max-w-7xl mx-auto">
              <Anim>
                <div>
                  <p className="section-eyebrow">About Our {data.town} Services</p>
                  <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-5 uppercase leading-tight">
                    Your Trusted Deck <br/><span className="text-lime">Contractor in {data.town}</span>
                  </h2>
                  <div className="space-y-4 text-stone leading-relaxed font-body">
                    <p>
                      DeckPros NJ provides professional deck construction, repair, and replacement services to homeowners in {data.town}, NJ. As a fully licensed and insured New Jersey deck contractor with over 25 years of experience, we deliver expert craftsmanship on every project — from custom composite installs to full wood deck rebuilds.
                    </p>
                    <p>
                      We work with Trex, TimberTech, and Azek composite decking, plus premium natural wood options like cedar, redwood, pressure-treated lumber, and exotic hardwoods. Whether you need a new deck built from scratch, structural repairs, or a full replacement — our {data.town} team handles design, permitting, and final inspection.
                    </p>
                    <p>
                      Every deck we build in {data.town} and throughout {data.county} is engineered for New Jersey's climate, meets all local codes, and is backed by our lifetime workmanship warranty.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-6 mb-6">
                    {[
                      'Licensed · Bonded · Insured',
                      'Lifetime Workmanship Warranty',
                      '25+ Years in New Jersey',
                      'Permits & HOA Handled',
                    ].map((item) => (
                      <div key={item} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-lime flex-shrink-0 mt-0.5" />
                        <span className="text-navy text-sm font-body font-medium">{item}</span>
                      </div>
                    ))}
                  </div>

                  <Link to="/contact">
                    <Button variant="cta" size="lg">
                      Get a Free {data.town} Quote
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </Anim>

              <Anim delay={120}>
                <AutoSlideshow town={data.town} />
              </Anim>
            </div>
          </div>
        </section>

        {/* ─── Services grid ──────────────────────────── */}
        <section className="section-compact bg-cream-dark">
          <div className="container mx-auto px-4">
            <Anim>
              <div className="text-center mb-10 max-w-2xl mx-auto">
                <p className="section-eyebrow justify-center">Deck Services</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
                  Services in <span className="text-lime">{data.town}</span>
                </h2>
                <p className="text-stone font-body">
                  From design to final board — every deck service under one roof.
                </p>
              </div>
            </Anim>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
              {services.map((svc, i) => (
                <Anim key={svc.title} delay={i * 60}>
                  <div className="group bg-white border-2 border-navy/10 hover:border-lime hover:-translate-y-1 hover:shadow-hard transition-all duration-200 p-6 h-full">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-6 h-0.5 bg-lime" />
                      <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-lime">
                        Service {String(i + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <h3 className="font-display text-base font-bold text-navy uppercase tracking-wide mb-2 group-hover:text-lime transition-colors">
                      {svc.title}
                    </h3>
                    <p className="text-stone text-sm leading-relaxed font-body">{svc.desc}</p>
                  </div>
                </Anim>
              ))}
            </div>

            <Anim>
              <div className="text-center mt-8">
                <Link to="/services">
                  <Button variant="cta" size="lg">
                    View All Services
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </Anim>
          </div>
        </section>

        {/* ─── Gallery ─────────────────────────────── */}
        <section className="section-compact bg-cream">
          <div className="container mx-auto px-4">
            <Anim>
              <div className="text-center mb-10 max-w-2xl mx-auto">
                <p className="section-eyebrow justify-center">Recent Builds</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
                  Deck Projects Near <span className="text-lime">{data.town}</span>
                </h2>
              </div>
            </Anim>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-6xl mx-auto">
              {data.images.map((src, i) => (
                <Anim key={i} delay={i * 60}>
                  <div className="border-2 border-navy/10 hover:border-lime hover:-translate-y-1 hover:shadow-hard transition-all duration-200 overflow-hidden">
                    <img
                      src={src}
                      alt={data.imageAlts[i]}
                      className="w-full aspect-[4/3] object-cover transition-transform duration-500 hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                </Anim>
              ))}
            </div>
            <Anim>
              <div className="text-center mt-8">
                <Link to="/gallery">
                  <Button variant="outline" size="lg">
                    View Full Gallery
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </Anim>
          </div>
        </section>

        {/* ─── Why Choose Us ────────────────────────── */}
        <section className="section-compact bg-navy text-white">
          <div className="container mx-auto px-4">
            <Anim>
              <div className="text-center mb-10 max-w-2xl mx-auto">
                <p className="section-eyebrow justify-center">Why DeckPros NJ</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold">
                  Why {data.town} Homeowners <span className="text-lime">Choose Us</span>
                </h2>
              </div>
            </Anim>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
              {reasons.map((r, i) => (
                <Anim key={r.title} delay={i * 60}>
                  <div className="relative bg-navy-deep border-2 border-white/10 hover:border-lime transition-colors p-6 h-full">
                    <div className="w-11 h-11 bg-lime flex items-center justify-center border-2 border-navy mb-4">
                      <r.icon className="w-5 h-5 text-navy" />
                    </div>
                    <h3 className="font-display text-base font-bold text-white uppercase tracking-wide mb-2">
                      {r.title}
                    </h3>
                    <p className="text-white/70 text-sm leading-relaxed font-body">{r.desc}</p>
                  </div>
                </Anim>
              ))}
            </div>
          </div>
        </section>

        {/* ─── FAQ with microdata ───────────────────── */}
        <section className="section-compact bg-cream-dark">
          <div className="container mx-auto px-4">
            <Anim>
              <div className="text-center mb-10 max-w-2xl mx-auto">
                <p className="section-eyebrow justify-center">Common Questions</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
                  Deck Building in <span className="text-lime">{data.town}</span>
                </h2>
                <p className="text-stone font-body">
                  Straight answers on permits, timing, materials, and cost.
                </p>
              </div>
            </Anim>

            <div
              className="max-w-4xl mx-auto space-y-3"
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              {faqs.map((faq, i) => (
                <details
                  key={i}
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  className="group bg-white border-2 border-navy/15 hover:border-lime transition-colors [&[open]]:border-lime [&[open]]:shadow-hard"
                >
                  <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none">
                    <h3
                      itemProp="name"
                      className="font-display text-sm md:text-base font-bold text-navy uppercase tracking-wide group-open:text-lime transition-colors"
                    >
                      {faq.question}
                    </h3>
                    <span className="font-display text-2xl text-lime leading-none transition-transform group-open:rotate-45 flex-shrink-0">+</span>
                  </summary>
                  <div
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                    className="px-5 pb-5 -mt-1"
                  >
                    <p itemProp="text" className="text-stone text-sm leading-relaxed font-body border-t border-navy/10 pt-4">
                      {faq.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </div>
  );
}
